import React, {useState} from "react";
import {useOrderApi} from "../context/ApiContext";
import SamButton from "./SamButton";
import {MarkAsInspectedFormData} from "../api/ApiTypes.ts";
import {createLocalDate, formatDateToString} from "../utils/dateUtils.ts";
import CancelButton from "./CancelButton.tsx";

interface MarkAsInspectedProps {
    orderId: number | null;
    onSuccess?: () => void;
    onCancel?: () => void;
    inspectionActualDate?: string;
    estimatedDeliveryDate?: string;
}

export const MarkAsInspected: React.FC<MarkAsInspectedProps> = ({
                                                                    orderId,
                                                                    onSuccess,
                                                                    onCancel,
                                                                    inspectionActualDate,
                                                                    estimatedDeliveryDate,
                                                                }) => {
    if (!orderId) {
        return <div>Invalid Order ID</div>;
    }
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);


    const isInspectionDateAfterDelivery = (newInspectionDate: Date, newDeliveryDate: Date): boolean => {
        if (newInspectionDate && newDeliveryDate) {
            return newInspectionDate > newDeliveryDate;
        }
        return false;
    }

    const [isLateDelivery, setIsLateDelivery] = useState<boolean>(isInspectionDateAfterDelivery(createLocalDate(inspectionActualDate), createLocalDate(estimatedDeliveryDate)));


    const [formData, setFormData] = useState<MarkAsInspectedFormData>({
        orderId: orderId,
        inspectionActualDate: formatDateToString(createLocalDate(inspectionActualDate)),
        inspectionActualTime: "",
        estimatedDeliveryDate: formatDateToString(createLocalDate(estimatedDeliveryDate)),
        orderStatusMessage: "",
        reasonLateDelivery: ""
    });

    const [errors, setErrors] = useState<{
        inspectionDate?: string;
        estimatedDeliveryDate?: string;
        reasonLateDelivery?: string;
    }>({});


    const orderApi = useOrderApi();



    // Handler for date changes using date picker
    const handleInspectionDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = createLocalDate(e.target.value)
        const isLate = isInspectionDateAfterDelivery(newDate, createLocalDate(formData.estimatedDeliveryDate));
        setFormData(prev => ({
            ...prev,
            inspectionActualDate: formatDateToString(newDate)
        }));
        setIsLateDelivery(isLate);
    };

    const handleDeliveryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = createLocalDate(e.target.value);
        const isLate = isInspectionDateAfterDelivery(createLocalDate(formData.inspectionActualDate), newDate);
        setFormData(prev => ({
            ...prev,
            estimatedDeliveryDate: formatDateToString(newDate)
        }));
        setIsLateDelivery(isLate);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing in late delivery reason
        if (name === "reasonLateDelivery" && value.trim()) {
            setErrors(prev => ({ ...prev, reasonLateDelivery: undefined }));
        }
    };

    const validateForm = (): boolean => {
        let isValid = true;
        const newErrors: {
            inspectionDate?: string;
            estimatedDeliveryDate?: string;
            reasonLateDelivery?: string;
        } = {};

        // Check required fields
        if (!formData.inspectionActualDate) {
            newErrors.inspectionDate = "Inspection date is required";
            isValid = false;
        }

        if (!formData.inspectionActualTime) {
            isValid = false;
        }

        if (!formData.estimatedDeliveryDate) {
            newErrors.estimatedDeliveryDate = "Estimated delivery date is required";
            isValid = false;
        }

        // Validate late delivery reason if inspection date is after delivery date
        if (isLateDelivery && !formData.reasonLateDelivery.trim()) {
            newErrors.reasonLateDelivery = "Please provide a reason for the late delivery date";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);

            // Call your API to mark as inspected
            await orderApi.markAsInspected(formData);

            // Call onSuccess to notify parent component if provided
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error("Error marking as inspected:", error);
            // Handle API error
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        // Prevent cancellation during submission
        if (!isSubmitting && onCancel) {
            onCancel();
        }
    };


    return (
        <div className="w-full max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
                <input type="hidden" name="orderId" value={formData.orderId} />

                <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="mark-as-inspected-inspection-date" className="block text-sm font-medium text-gray-700 mb-1">
                            Inspection Date
                        </label>
                        <input
                            required
                            type="date"
                            id="mark-as-inspected-inspection-date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={formData.inspectionActualDate}
                            onChange={handleInspectionDateChange}
                        />
                        {errors.inspectionDate && (
                            <p className="mt-1 text-sm text-red-600">{errors.inspectionDate}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="inspection-time" className="block text-sm font-medium text-gray-700 mb-1">
                            Inspection Time
                        </label>
                        <select
                            required
                            id="inspection-time"
                            name="inspectionActualTime"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={formData.inspectionActualTime}
                            onChange={handleChange}
                        >
                            <option value=""></option>
                            <option value="07:00:00">7:00am</option>
                            <option value="07:30:00">7:30am</option>
                            <option value="08:00:00">8:00am</option>
                            <option value="08:30:00">8:30am</option>
                            <option value="09:00:00">9:00am</option>
                            <option value="09:30:00">9:30am</option>
                            <option value="10:00:00">10:00am</option>
                            <option value="10:30:00">10:30am</option>
                            <option value="11:00:00">11:00am</option>
                            <option value="11:30:00">11:30am</option>
                            <option value="12:00:00">12:00pm</option>
                            <option value="12:30:00">12:30pm</option>
                            <option value="13:00:00">1:00pm</option>
                            <option value="13:30:00">1:30pm</option>
                            <option value="14:00:00">2:00pm</option>
                            <option value="14:30:00">2:30pm</option>
                            <option value="15:00:00">3:00pm</option>
                            <option value="15:30:00">3:30pm</option>
                            <option value="16:00:00">4:00pm</option>
                            <option value="16:30:00">4:30pm</option>
                            <option value="17:00:00">5:00pm</option>
                            <option value="17:30:00">5:30pm</option>
                            <option value="18:00:00">6:00pm</option>
                            <option value="18:30:00">6:30pm</option>
                            <option value="19:00:00">7:00pm</option>
                            <option value="19:30:00">7:30pm</option>
                            <option value="20:00:00">8:00pm</option>
                        </select>
                    </div>
                </fieldset>

                <div>
                    <label htmlFor="mark-as-inspected-estimated-delivery-date" className="block text-sm font-medium text-gray-700 mb-1">
                        Estimated Delivery Date
                    </label>
                    <input
                        required
                        type="date"
                        id="mark-as-inspected-estimated-delivery-date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={formData.estimatedDeliveryDate}
                        onChange={handleDeliveryDateChange}
                    />
                    {errors.estimatedDeliveryDate && (
                        <p className="mt-1 text-sm text-red-600">{errors.estimatedDeliveryDate}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="mark-as-inspected-status-message" className="block text-sm font-medium text-gray-700 mb-1">
                        Status Message
                    </label>
                    <input
                        type="text"
                        name="orderStatusMessage"
                        id="mark-as-inspected-status-message"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={formData.orderStatusMessage}
                        onChange={handleChange}
                    />
                </div>

                {isLateDelivery && (
                    <div>
                        <label htmlFor="reasonLateDelivery" className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Late Delivery Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            required
                            type="text"
                            id="reasonLateDelivery"
                            name="reasonLateDelivery"
                            className={`w-full px-3 py-2 border ${errors.reasonLateDelivery ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                            value={formData.reasonLateDelivery}
                            onChange={handleChange}
                        />
                        {errors.reasonLateDelivery && (
                            <p className="mt-1 text-sm text-red-600">{errors.reasonLateDelivery}</p>
                        )}
                    </div>
                )}

                <div className="flex justify-end space-x-2">
                    <CancelButton
                        type="button"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </CancelButton>
                    <SamButton
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-t-transparent border-white rounded-full mr-1"></span>
                                <span>Saving...</span>
                            </>
                        ) : "Save"}
                    </SamButton>
                </div>
            </form>
        </div>
    );
};